import { enableKeyIsValid } from "@aidkitorg/types/lib/eval";
import { ExpandedSurvey } from "@aidkitorg/types/lib/survey";
import { ChangeEventHandler, useMemo, useState } from "react";
import { buildTemplatedComponentPathMap, findAndGoToComponent, templatedComponentKey } from "../ConfigSearch";
import { QueryChecker } from "./QueryChecker";
import { expandTemplatesStrict, extractNotificationsAndPaymentsWithSearchTrees } from "@aidkitorg/types/lib/translation/v0_to_legacy";

const goToPaymentsTab = async (paymentName: string) => {
  const tab = document.getElementsByTagName('distro-editor')[0].shadowRoot!.querySelector("[data-tab-name=payments]");
  if (tab) tab.dispatchEvent(new Event('click', { bubbles: true }));
  await new Promise(r => setTimeout(r, 50));
  document.getElementsByTagName('distro-editor')[0].shadowRoot!
    .querySelector(`[data-name="${paymentName}"]`)?.scrollIntoView({ block: 'center' });
}
function PaymentDetail({ task, survey }: { task: NonNullable<ExpandedSurvey['payments']>[number] & { searchTree?: string[] }, survey: ExpandedSurvey['survey'] }) {
  return <QueryChecker
    component={task}
    name={task.name}
    onNameClick={() => task.searchTree ? findAndGoToComponent(task.searchTree!, survey) : goToPaymentsTab(task.name)}
    expr={task.condition}
    targetField={task.targetField}
    testName="Eligible"
  />
}

export default function PaymentsEditor({ survey } : { survey: ExpandedSurvey }) {

  const payments = useMemo(() => {
    // TODO: We expand templates a gajillion times
    // Rows are extracted from the EXPANDED survey (one per iteration). A row that came from a
    // Templated Block has no un-expanded location of its own, so point its scroll-to at the single
    // un-expanded component the editor renders — descending into the block to the actual payment,
    // like the main-editor search does. Non-templated rows keep the precise path extract already
    // built for them.
    const templatedPaths = buildTemplatedComponentPathMap(survey.survey);
    const extractedNotifsAndPayments = extractNotificationsAndPaymentsWithSearchTrees(
      expandTemplatesStrict(survey.survey)
    );
    const extractedPayments = extractedNotifsAndPayments[1].map((p) => {
      const blockId = (p as any)._parentTemplatedBlockId;
      const identifier = (p as any)._preSubstitutionIdentifier;
      const rawTree = blockId && identifier ? templatedPaths[templatedComponentKey(blockId, identifier)] : undefined;
      return rawTree ? { ...p, searchTree: rawTree } : p;
    });
    return [...(survey.payments || []), ...extractedPayments]
  }, [survey]);

  if (!payments?.length) {
    return <div className="mt-4 ml-1">No payments yet!</div>
  }
  
  const [search, setSearch] = useState("");
  const [enabledFilter, setEnabledFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  const filteredResults = useMemo(() => {
    const searchFiltered = (payments ?? []).filter(
      item => item.name.toLowerCase().includes(search.toLowerCase())
        || item.targetField.toLowerCase().includes(search.toLowerCase())
    );
    if (enabledFilter === 'all') return searchFiltered;
    return searchFiltered.filter(p => {
      const isEnabled = enableKeyIsValid(p.enableKey, p);
      return enabledFilter === 'enabled' ? isEnabled : !isEnabled;
    });
  }, [search, payments, enabledFilter]);
  const enabled = useMemo(() =>
    filteredResults?.filter(p => enableKeyIsValid(p.enableKey, p)).length || 0
  , [filteredResults]);
  const onChangeSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearch(e.target.value);
    const pane = document.getElementById("top-of-pane");
    if (pane) pane.scrollIntoView();
  }

  return <div>
    <div id="top-of-pane" />
    <input type="search" placeholder="Search for payments" value={search} onChange={onChangeSearch}
      className="block w-full border top-0 left-0 z-50 p-2 mt-1 mb-1 sticky sm:text-sm rounded-md" />
    <div className="flex items-center justify-center gap-1 my-2">
      {(['all', 'enabled', 'disabled'] as const).map(option => (
        <button
          key={option}
          onClick={() => {
            setEnabledFilter(option);
            const pane = document.getElementById("top-of-pane");
            if (pane) pane.scrollIntoView();
          }}
          className={`px-3 py-1 text-sm rounded-full border capitalize ${
            enabledFilter === option
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          {option}
        </button>
      ))}
    </div>
    <h4 className="text-center">{enabled}/{filteredResults?.length || 0} Enabled</h4>
    {filteredResults.map((t) => <PaymentDetail key={t.name} task={t} survey={survey.survey} />)}
  </div>
}
